/** @jsxImportSource @emotion/react */
import { useSession } from '@quickbit/backoffice-core-frontend';
import { Button, Modal, Select, useTheme } from '@quickbit/qb-design-system';
import { createContext, ReactNode, useContext, useState } from 'react';
import { useAsync, useSessionStorage } from 'react-use';
import { getOrganisation, PlatformMerchant } from './api';

const ORGANISATION_SESSION_STORAGE_KEY = 'qbOrganisation';

interface OrganisationContextType {
  isPlatform: boolean;
  selectedMerchant: PlatformMerchant;
  merchants: PlatformMerchant[];
  needsKyb: boolean;
}

const OrganisationContext = createContext<OrganisationContextType>({
  isPlatform: false,
  selectedMerchant: { merchantId: '', name: '', status: 'ACTIVE' },
  merchants: [],
  needsKyb: false,
});

export const useOrganisation = () => {
  return useContext(OrganisationContext);
};

export const OrganisationProvider = ({ children }: { children: ReactNode }) => {
  const { spacing } = useTheme();
  const session = useSession();
  const [showMerchantSelect, setShowMerchantSelect] = useState<boolean>(false);
  const [organisation, setOrganisation] =
    useSessionStorage<OrganisationContextType>(
      ORGANISATION_SESSION_STORAGE_KEY,
      {
        isPlatform: false,
        selectedMerchant: { merchantId: '', name: '', status: 'ACTIVE' },
        merchants: [],
        needsKyb: false,
      }
    );
  const { value } = useAsync(async () => {
    if (!session.authenticated) return null;
    const orgs = await getOrganisation();
    if (
      !organisation.selectedMerchant.merchantId ||
      !orgs.merchants.find(
        ({ merchantId }) =>
          merchantId === organisation.selectedMerchant.merchantId
      ) ||
      orgs.needsKyb
    ) {
      setOrganisation({
        isPlatform: orgs.isPlatform,
        selectedMerchant: orgs.merchants[0] ?? {
          merchantId: '',
          name: '',
          status: 'ACTIVE',
        },
        merchants: orgs.merchants,
        needsKyb: orgs.needsKyb,
      });
    }
    return orgs;
  }, [session]);

  const merchantsOptions = value?.merchants.map(({ merchantId, name }) => ({
    value: merchantId,
    label: name,
  }));

  return (
    <OrganisationContext.Provider value={organisation}>
      {value?.isPlatform && merchantsOptions && (
        <div
          css={{
            position: 'fixed',
            zIndex: 99,
            bottom: spacing.l,
            left: spacing.xs,
            width: '190px',
          }}
        >
          {!showMerchantSelect && (
            <Button
              variant="secondary"
              css={{ width: '100%' }}
              text={
                merchantsOptions.find(
                  (m) => m.value === organisation.selectedMerchant.merchantId
                )?.label
              }
              onClick={() => {
                setShowMerchantSelect(true);
              }}
              endIcon="building"
            />
          )}
          {showMerchantSelect && (
            <Modal
              closeModal={() => {
                setShowMerchantSelect(false);
              }}
              subtitle="Select which merchant you want to view"
            >
              <Select
                label="Merchant"
                values={
                  organisation.selectedMerchant.merchantId
                    ? [organisation.selectedMerchant.merchantId]
                    : []
                }
                options={merchantsOptions}
                onSelect={(selection: string[]) => {
                  if (selection.length !== 0) {
                    setOrganisation({
                      ...organisation,
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      selectedMerchant: value.merchants.find(
                        ({ merchantId }) => merchantId === selection[0]
                      )!,
                    });
                  }
                  setShowMerchantSelect(false);
                }}
              />
            </Modal>
          )}
        </div>
      )}
      {children}
    </OrganisationContext.Provider>
  );
};
